import { createFileRoute, Link } from "@tanstack/react-router";
import { canonical, ROOFING_CONTRACTOR_ID } from "@/components/site/site-config";
import { Breadcrumbs, breadcrumbListJsonLd } from "@/components/site/Breadcrumbs";
import { BUSINESS } from "@/components/site/constants";
import { LECANTO_METAL_CASE_STUDY as study } from "@/components/site/case-studies";
import {
  CaseStudyHero,
  FactCard,
  BeforeAfterSection,
  RelatedServices,
  FaqSection,
  CaseStudyForm,
  RelatedProjects,
  CaseStudyFinalCta,
} from "@/components/site/CaseStudy";

const PATH = `/projects/${study.slug}`;

const CRUMBS = [
  { label: "Roofing Projects", href: "/projects" },
  { label: study.breadcrumbLabel, href: PATH },
];

export const Route = createFileRoute("/projects/lecanto-metal-roof-before-after")({
  head: () => ({
    meta: [
      { title: study.title },
      { name: "description", content: study.metaDescription },
      { name: "robots", content: "index,follow" },
      { property: "og:title", content: study.title },
      { property: "og:description", content: study.metaDescription },
      { property: "og:url", content: canonical(PATH) },
      { property: "og:type", content: "article" },
      { property: "og:image", content: study.ogImage },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: study.ogImage },
    ],
    links: [{ rel: "canonical", href: canonical(PATH) }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify([
          breadcrumbListJsonLd(CRUMBS),
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: study.faqs.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          },
          {
            "@context": "https://schema.org",
            "@type": "Service",
            name: "Metal Roof Replacement in Lecanto, Florida",
            serviceType: "Metal Roofing",
            url: canonical(PATH),
            provider: { "@id": ROOFING_CONTRACTOR_ID },
            areaServed: { "@type": "City", name: "Lecanto, Florida" },
          },
        ]),
      },
    ],
  }),
  component: Page,
});

function Page() {
  return (
    <>
      <Breadcrumbs items={CRUMBS} />
      <CaseStudyHero study={study} />
      <div className="mx-auto max-w-4xl px-4 py-10 md:py-14 prose prose-slate max-w-none lg:prose-lg">
        <FactCard study={study} heading="Lecanto Metal Roofing Project Overview" />

        <BeforeAfterSection study={study} heading="Lecanto Metal Roof Before and After" />

        <h2>What We Look for on an Aging Metal Roof</h2>
        <p>
          These photographs show a deteriorated metal roofing section before work and the completed
          replacement section afterward. They are useful for showing conditions, but they are not a
          diagnosis. When Destination Roofing evaluates an aging metal roof in Lecanto, the
          inspection commonly considers:
        </p>
        <ul>
          <li>Panel corrosion or deterioration</li>
          <li>Backing-out screws</li>
          <li>Failed fastener washers</li>
          <li>Loose or missing fasteners</li>
          <li>Failed sealant</li>
          <li>Leaks around penetrations</li>
          <li>Damaged trim or flashing</li>
          <li>Previous patching</li>
          <li>Whether panels can still hold replacement fasteners</li>
          <li>Condition of the roof deck and surrounding roofing system</li>
        </ul>
        <p>
          The correct recommendation depends on the condition of the entire roofing system. A
          photograph can show visible concerns, but an on-site inspection is needed to determine
          whether repair, re-screwing, or replacement is appropriate.
        </p>
        {study.ownerCommentary && (
          <blockquote>
            <p>{study.ownerCommentary}</p>
            <footer>— Deston Judy, owner and licensed roofing contractor</footer>
          </blockquote>
        )}

        <h2>Could the Metal Roof Be Repaired or Re-Screwed?</h2>
        <h3>Limited repair</h3>
        <p>
          A limited repair may make sense when the leak or damage is isolated and the surrounding
          panels remain serviceable. That is often the starting point for a{" "}
          <Link to="/roof-repair">metal roof leak repair</Link>.
        </p>
        <h3>Complete re-screw</h3>
        <p>
          A re-screw may be appropriate when fastener problems are widespread but the panels remain
          structurally sound and are not widely rusted. During a complete re-screw:
        </p>
        <ul>
          <li>Existing screws are removed.</li>
          <li>Wider-diameter long-life ZAC screws are installed.</li>
          <li>The wider fastener properly engages the existing hole.</li>
          <li>Penetrations and vulnerable details are resealed.</li>
          <li>Appropriate wood-to-metal or metal-to-metal fasteners are selected.</li>
        </ul>
        <h3>Replacement</h3>
        <p>Replacement becomes more practical when:</p>
        <ul>
          <li>Panels are widely rusted</li>
          <li>Metal is severely deteriorated</li>
          <li>New fasteners cannot hold reliably</li>
          <li>Leaks occur across multiple roof areas</li>
          <li>Previous repairs repeatedly fail</li>
          <li>The roof needs broader correction</li>
        </ul>
        <p>
          Destination Roofing does not automatically recommend full replacement when a proper repair
          or re-screw can reasonably restore the roof. Learn more about{" "}
          <Link to="/metal-roofing">metal roofing systems, repairs, and re-screws</Link>.
        </p>

        <h2>Details That Matter During Metal-Roof Replacement</h2>
        <h3>Underlayment</h3>
        <p>
          Destination Roofing installs high-temperature peel-and-stick underlayment under new metal
          roofing.
        </p>
        <h3>Fasteners</h3>
        <p>
          Fastener type depends on the metal system and substrate. Exposed-fastener systems require
          correct screw placement and washer compression.
        </p>
        <h3>Flashing and trim</h3>
        <p>
          New metal trim and flashing should be integrated with the panel system at eaves, gables,
          ridges, valleys, walls, and transitions.
        </p>
        <h3>Penetrations</h3>
        <p>
          Pipe penetrations, electrical risers, vents, chimneys, and other roof openings require
          compatible boots, flashing, and sealant.
        </p>
        <h3>Drainage</h3>
        <p>
          Metal roof sections must be detailed so water can move toward the intended drainage path
          without being trapped behind flashing, transitions, or roof details.
        </p>

        <h2>How Destination Roofing Handles Metal-Roof Projects</h2>
        <ol>
          <li>
            <strong>Homeowner contact.</strong> Liz or Deston receives the request and creates a
            work order in AccuLynx.
          </li>
          <li>
            <strong>Free inspection.</strong> Deston generally performs the inspection personally.
          </li>
          <li>
            <strong>Roof evaluation.</strong> The panels, fasteners, sealants, penetrations,
            flashing, and roof condition are evaluated.
          </li>
          <li>
            <strong>Honest recommendation.</strong> The homeowner receives an explanation of whether
            repair, re-screwing, or replacement appears most reasonable.
          </li>
          <li>
            <strong>Written estimate.</strong> The estimate is commonly prepared and explained at
            the property.
          </li>
          <li>
            <strong>Roofing work.</strong> The approved work is completed using materials and
            methods appropriate for the roof system.
          </li>
          <li>
            <strong>Final review.</strong> Deston reviews workmanship and cleanup, and final
            photographs are taken.
          </li>
        </ol>

        <RelatedServices study={study} heading="Related Roofing Services" />

        <h2>A Local, Owner-Led Roofing Company</h2>
        <p>
          Destination Roofing Inc. is a local, family-owned roofing company serving Lecanto and
          communities throughout Citrus County. Owner and licensed roofing contractor Deston Judy
          personally performs many inspections, explains estimates, checks project quality, and
          remains available throughout the work.
        </p>
        <ul>
          <li>Florida roofing license #{" "}{BUSINESS.licenseNumber}</li>
          <li>More than 100 roofs completed by Destination Roofing</li>
          <li>Roofing experience beginning at age 15</li>
          <li>Approximately 15 years rooted in Citrus County</li>
        </ul>
        <p>
          <Link to="/about">More about Deston and Destination Roofing →</Link>
        </p>

        <FaqSection study={study} heading="Lecanto Metal-Roofing Questions" />

        <CaseStudyForm study={study} />

        <RelatedProjects study={study} heading="More Destination Roofing Projects" />

        <CaseStudyFinalCta study={study} />
      </div>
    </>
  );
}
